import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import gamesService from "../../services/api/games-service";

const styles = {
    pageWrapper: { background: "#f9fafb", minHeight: "100vh", paddingBottom: 40 },
    container: { maxWidth: 1200, margin: "0 auto", padding: "24px 20px" },
    headerBar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 },
    title: { fontSize: 20, fontWeight: 700, color: "#111827" },
    filtersSection: { background: "#fff", border: "1px solid #e6e7eb", borderRadius: 10, padding: 14, marginBottom: 20 },
    filtersGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 },
    filterLabel: { display: "flex", flexDirection: "column", fontWeight: 600, color: "#374151", fontSize: 13, gap: 4 },
    filterInput: { borderRadius: 8, border: "1px solid #d1d5db", padding: "10px 12px", fontSize: 14, background: "#fff" },
    filterActions: { display: "flex", justifyContent: "flex-end", marginTop: 12, gap: 8 },
    resetBtn: { background: "#fff", border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 14px", fontSize: 14, fontWeight: 600, cursor: "pointer", color: "#6b7280" },
    card: { background: "#fff", border: "1px solid #e6e7eb", borderRadius: 10, boxShadow: "0 2px 6px rgba(0,0,0,0.04)" },
    table: { width: "100%", borderCollapse: "separate", borderSpacing: 0, fontSize: 14 },
    thead: { background: "#fbfbfc" },
    th: { padding: "12px 16px", textAlign: "left", fontWeight: 700, fontSize: 13, color: "#374151", borderBottom: "1px solid #eef2f7" },
    td: { padding: "12px 16px", borderBottom: "1px solid #f3f4f6", color: "#111827", verticalAlign: "top" },
    pagination: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", background: "#fff", borderTop: "1px solid #e6e7eb" },
    paginationInfo: { fontSize: 13, fontWeight: 600, color: "#4b5563" },
    pageBtn: { background: "#fff", border: "1px solid #d1d5db", borderRadius: 8, padding: "8px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer", minWidth: 80, color: "#374151" },
    chartCard: { background: "#fff", border: "1px solid #e6e7eb", borderRadius: 10, padding: 14 },
    chartsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 },
    detailsBtn: { cursor: "pointer", color: "#2563eb", border: "1px solid #60a5fa", background: "#eff6ff", padding: "6px 10px", borderRadius: 8, fontWeight: 600, fontSize: 13 },
    exportBtn: { background: "#10b981", border: 0, color: "#fff", borderRadius: 8, padding: "8px 12px", fontSize: 14, fontWeight: 700, cursor: "pointer" },
    modalOverlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050, padding: 20 },
    modalContent: { background: "#fff", borderRadius: 12, width: "100%", maxWidth: 720, maxHeight: "90vh", overflow: "auto", boxShadow: "0 30px 60px rgba(2,6,23,0.2)" },
    modalHeader: { padding: "18px 22px", borderBottom: "1px solid #eef2f7", display: "flex", justifyContent: "space-between", alignItems: "center" },
    modalBody: { padding: "20px 22px" },
};

const SCORE_TYPES = [
    { label: "Sum", value: "sum" },
    { label: "Average", value: "avg" },
    { label: "Max", value: "max" },
    { label: "Min", value: "min" },
    { label: "Count", value: "value_count" },
];

const STATUS_FILTERS = [
    { label: "All", value: "" },
    { label: "Active", value: "active" },
    { label: "Deleted", value: "deleted" },
];

const calcTier = (score) => {
    if (score >= 1000) return "Gold";
    if (score >= 500) return "Silver";
    return "Bronze";
};

const LineChart = ({ data }) => {
    const points = useMemo(() => {
        const max = Math.max(1, ...data.map((d) => d.doc_count || 0));
        const width = 520;
        const height = 160;
        const step = data.length > 1 ? width / (data.length - 1) : width;
        return data.map((d, i) => {
            const x = i * step;
            const y = height - (height * (d.doc_count || 0)) / max;
            return `${x},${y}`;
        }).join(" ");
    }, [data]);
    return (
        <svg width="100%" height="180" viewBox="0 0 520 180">
            <polyline fill="none" stroke="#6366f1" strokeWidth="2" points={points} />
        </svg>
    );
};

const PieChart = ({ buckets }) => {
    const total = Math.max(1, buckets.reduce((s, b) => s + (b.doc_count || 0), 0));
    let acc = 0;
    const colors = ["#6366f1", "#10b981", "#f59e0b", "#ef4444", "#06b6d4", "#8b5cf6", "#84cc16"];
    const slices = buckets.slice(0, 7).map((b, i) => {
        const start = acc / total;
        acc += b.doc_count || 0;
        const end = acc / total;
        return `${Math.round(start * 360)}deg ${Math.round(end * 360)}deg ${colors[i % colors.length]}`;
    });
    const bg = slices.map((s) => s.split(" ")[2]).join(", ");
    // approximate pie using conic-gradient
    return (
        <div>
            <div style={{ width: 180, height: 180, borderRadius: "50%", background: `conic-gradient(${buckets.slice(0,7).map((b,i)=>`${colors[i%colors.length]} 0 ${(acc?Math.round(((b.doc_count||0)/total)*360):0)}deg`).join(', ')})` }} />
            <div style={{ marginTop: 8 }}>
                {buckets.slice(0, 7).map((b, i) => (
                    <div key={b.key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                        <span style={{ width: 10, height: 10, background: colors[i % colors.length], display: "inline-block", borderRadius: 2 }} />
                        <span>{b.key} — {b.doc_count}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const LeaderboardDashboard = (props) => {
    const search = new URLSearchParams(props.location?.search || window.location.search);
    const projectId = search.get("projectid") || "";

    const [gameTypes, setGameTypes] = useState([{ label: "All", value: "all" }]);
    const [gameType, setGameType] = useState("all");
    const [gameId, setGameId] = useState("");
    const [dateFrom, setDateFrom] = useState(moment().subtract(7, 'days').format('YYYY-MM-DD'));
    const [dateTo, setDateTo] = useState(moment().format('YYYY-MM-DD'));
    const [scoreType, setScoreType] = useState("sum");
    const [sortOrder, setSortOrder] = useState("desc");
    const [limit, setLimit] = useState(10);
    const [statusFilter, setStatusFilter] = useState("");
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState({ total: 0, page: 1, total_pages: 1, page_size: 10, from: 0 });
    const [charts, setCharts] = useState({ new_users: [], top_games: [] });
    const [userModal, setUserModal] = useState({ open: false, data: null });

    useEffect(() => {
        if (!projectId) return;
        gamesService.getGamesSettings({ project_id: projectId }).then((resp) => {
            if (resp?.status) {
                const enabled = resp?.data?.enabled_games || [];
                const opts = [{ label: "All", value: "all" }].concat(enabled.map((t) => ({ label: t.replace(/_/g, ' '), value: t })));
                setGameTypes(opts);
            }
        }).catch(() => {});
    }, [projectId]);

    const hasActiveFilters = useMemo(() => !!(gameType && gameType !== 'all') || !!gameId || !!statusFilter || !!dateFrom || !!dateTo || scoreType !== 'sum' || sortOrder !== 'desc', [gameType, gameId, statusFilter, dateFrom, dateTo, scoreType, sortOrder]);

    const loadData = async () => {
        if (!projectId) return;
        // enforce max 31 days
        const start = moment(dateFrom);
        const end = moment(dateTo);
        if (end.diff(start, 'days') > 31) {
            toast.error('Date range cannot exceed 31 days');
            return;
        }
        setLoading(true);
        try {
            const from = (page - 1) * Number(limit);
            const payload = {
                project_id: projectId,
                game_type: gameType !== 'all' ? gameType : undefined,
                game_id: gameId?.trim() || undefined,
                start_date: moment(dateFrom).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
                end_date: moment(dateTo).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
                score_type: scoreType,
                sort_order: sortOrder,
                from,
                size: Number(limit),
            };
            const [lbResp, chResp] = await Promise.all([
                gamesService.getLeaderboard(payload),
                gamesService.getLeaderboardCharts(payload),
            ]);

            if (lbResp?.status) {
                let items = lbResp.data?.leaderboard || lbResp?.data?.data?.leaderboard || [];
                if (statusFilter === 'active') items = items.filter((r) => r.status !== false);
                if (statusFilter === 'deleted') items = items.filter((r) => r.status === false);
                setRows(items);
                setMeta(lbResp.data?.meta || lbResp?.data?.data?.meta || { total: 0, page, total_pages: 1, from, page_size: Number(limit) });
            } else {
                toast.error(lbResp?.error?.message || 'Failed to load leaderboard');
            }

            if (chResp?.status) {
                setCharts(chResp.data || chResp?.data?.data || { new_users: [], top_games: [] });
            }
        } catch (e) {
            toast.error('Failed to load leaderboard');
        } finally {
            setLoading(false);
        }
    };

    // Debounce filter changes
    useEffect(() => {
        const t = setTimeout(() => {
            setPage(1);
        }, 250);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameType, gameId, dateFrom, dateTo, scoreType, sortOrder, limit, statusFilter]);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, page]);

    const exportCSV = () => {
        const headers = ["Rank", "User Name", "Email", "Score", "Tier", "Current Streak", "High Streak", "Status"];
        const lines = rows.map((r) => [r.rank, r.user_name || '', r.email || '', r.score || 0, calcTier(Number(r.score || 0)), r.streak ?? 0, r.max_streak ?? 0, r.status === false ? 'Deleted' : 'Active'].map((x) => `"${String(x).replace(/"/g, '""')}"`).join(","));
        const csv = [headers.join(","), ...lines].join("\n");
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `leaderboard_${projectId}_${moment().format('YYYYMMDD_HHmmss')}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const startIndex = Math.min(meta.total, (meta.page - 1) * meta.page_size + 1);
    const endIndex = Math.min(meta.total, (meta.page - 1) * meta.page_size + rows.length);

    const openUserModal = async (user_id) => {
        try {
            const resp = await gamesService.getGamesUserDetails({ project_id: projectId, user_id });
            if (resp?.status) {
                setUserModal({ open: true, data: resp.data });
            } else {
                toast.error(resp?.error?.message || 'Failed to fetch user details');
            }
        } catch (e) {
            toast.error('Failed to fetch user details');
        }
    };

    return (
        <div style={styles.pageWrapper} className="content bg-light">
            <ToastContainer position="top-right" autoClose={3000} />
            <div style={styles.container}>
                <div style={styles.headerBar}>
                    <h2 style={styles.title}>🏆 Leaderboard Dashboard</h2>
                    <button style={styles.exportBtn} onClick={exportCSV}>Export CSV</button>
                </div>

                <div style={styles.filtersSection}>
                    <div style={styles.filtersGrid}>
                        <label style={styles.filterLabel}>
                            Project ID
                            <input style={styles.filterInput} value={projectId} readOnly />
                        </label>
                        <label style={styles.filterLabel}>
                            Game Type
                            <select style={styles.filterInput} value={gameType} onChange={(e) => setGameType(e.target.value)}>
                                {gameTypes.map((g) => (
                                    <option key={g.value} value={g.value}>{g.label}</option>
                                ))}
                            </select>
                        </label>
                        <label style={styles.filterLabel}>
                            Game ID
                            <input style={styles.filterInput} value={gameId} onChange={(e) => setGameId(e.target.value)} placeholder="Optional" />
                        </label>
                        <label style={styles.filterLabel}>
                            From Date
                            <input type="date" style={styles.filterInput} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                        </label>
                        <label style={styles.filterLabel}>
                            To Date
                            <input type="date" style={styles.filterInput} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                        </label>
                        <label style={styles.filterLabel}>
                            Score Type
                            <select style={styles.filterInput} value={scoreType} onChange={(e) => setScoreType(e.target.value)}>
                                {SCORE_TYPES.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
                            </select>
                        </label>
                        <label style={styles.filterLabel}>
                            Sort Order
                            <select style={styles.filterInput} value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
                                <option value="desc">Desc</option>
                                <option value="asc">Asc</option>
                            </select>
                        </label>
                        <label style={styles.filterLabel}>
                            Page Size
                            <select style={styles.filterInput} value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                                {[10,20,50,100].map((n)=>(<option key={n} value={n}>{n}</option>))}
                            </select>
                        </label>
                        <label style={styles.filterLabel}>
                            Status
                            <select style={styles.filterInput} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                {STATUS_FILTERS.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
                            </select>
                        </label>
                    </div>
                    {hasActiveFilters && (
                        <div style={styles.filterActions}>
                            <button style={styles.resetBtn} onClick={() => {
                                setGameType('all');
                                setGameId('');
                                setDateFrom(moment().subtract(7, 'days').format('YYYY-MM-DD'));
                                setDateTo(moment().format('YYYY-MM-DD'));
                                setScoreType('sum');
                                setSortOrder('desc');
                                setLimit(10);
                                setStatusFilter('');
                            }}>Reset Filters</button>
                        </div>
                    )}
                </div>

                <div className="row" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
                    <div style={styles.card}>
                        <div style={{ overflowX: "auto" }}>
                            <table style={styles.table}>
                                <thead style={styles.thead}>
                                    <tr>
                                        <th style={styles.th}>Rank</th>
                                        <th style={styles.th}>User</th>
                                        <th style={styles.th}>Score</th>
                                        <th style={styles.th}>Tier</th>
                                        <th style={styles.th}>Streak</th>
                                        <th style={styles.th}>High streak</th>
                                        <th style={styles.th}>Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td style={{ ...styles.td, textAlign: 'center' }} colSpan={7}>Loading...</td></tr>
                                    ) : rows.length === 0 ? (
                                        <tr><td style={{ ...styles.td, textAlign: 'center' }} colSpan={7}>No data</td></tr>
                                    ) : rows.map((r) => (
                                        <tr key={`${r.user_id}-${r.rank}`}>
                                            <td style={styles.td}>{r.rank}</td>
                                            <td style={{ ...styles.td, color: r.status === false ? '#b91c1c' : '#111827' }}>
                                                <div style={{ fontWeight: 700 }}>{r.user_name || 'Anonymous'}</div>
                                                <div style={{ fontSize: 12, color: '#6b7280' }}>{r.email || ''}</div>
                                            </td>
                                            <td style={styles.td}>{Number(r.score || 0).toFixed(2)}</td>
                                            <td style={styles.td}>{calcTier(Number(r.score || 0))}</td>
                                            <td style={styles.td}>{r.streak ?? 0}</td>
                                            <td style={styles.td}>{r.max_streak ?? 0}</td>
                                            <td style={styles.td}><button style={styles.detailsBtn} onClick={() => openUserModal(r.user_id)}>View</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={styles.pagination}>
                            <div style={styles.paginationInfo}>Showing {startIndex}-{endIndex} of {meta.total} · Page {meta.page} of {meta.total_pages}</div>
                            <div style={{ display: "flex", gap: 8 }}>
                                <button style={styles.pageBtn} disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))}>← Prev</button>
                                <button style={styles.pageBtn} disabled={page >= meta.total_pages || loading} onClick={() => setPage((p) => p + 1)}>Next →</button>
                            </div>
                        </div>
                    </div>

                    <div style={styles.chartsGrid}>
                        <div style={styles.chartCard}>
                            <div style={{ fontWeight: 700, marginBottom: 8 }}>New Users Over Time</div>
                            <LineChart data={(charts.new_users || []).map((b) => ({ date: b.key_as_string || b.key, doc_count: b.doc_count }))} />
                        </div>
                        <div style={styles.chartCard}>
                            <div style={{ fontWeight: 700, marginBottom: 8 }}>Most Played Games</div>
                            <PieChart buckets={charts.top_games || []} />
                        </div>
                    </div>
                </div>
            </div>

            {userModal.open && (
                <div style={styles.modalOverlay} onClick={() => setUserModal({ open: false, data: null })}>
                    <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <div style={{ fontWeight: 700 }}>User Details</div>
                            <button style={{ background: 'transparent', border: 0, fontSize: 22, cursor: 'pointer' }} onClick={() => setUserModal({ open: false, data: null })}>×</button>
                        </div>
                        <div style={styles.modalBody}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                <div><strong>User ID:</strong> {userModal.data?.user_id || '-'}</div>
                                <div><strong>Name:</strong> {userModal.data?.user_name || '-'}</div>
                                <div><strong>Email:</strong> {userModal.data?.email || '-'}</div>
                                <div><strong>Status:</strong> {userModal.data?.status === false ? 'Deleted' : 'Active'}</div>
                                <div><strong>Country:</strong> {userModal.data?.country || '-'}</div>
                                <div><strong>Region:</strong> {userModal.data?.region || '-'}</div>
                                <div><strong>City:</strong> {userModal.data?.city || '-'}</div>
                                <div><strong>Current streak:</strong> {userModal.data?.streak ?? '-'}</div>
                                <div><strong>High streak:</strong> {userModal.data?.max_streak ?? '-'}</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LeaderboardDashboard;


